import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';

interface LoyaltyFlashRewardProps {
  styleVariant?: string;
}

function useCountdown(endsAt: string): string {
  const [display, setDisplay] = useState('');

  useEffect(() => {
    function calc() {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) {
        setDisplay('Expired');
        return;
      }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);

      if (d > 0) {
        setDisplay(`${d}d ${h}h ${m}m ${s}s`);
      } else {
        setDisplay(`${h}h ${m}m ${s}s`);
      }
    }

    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [endsAt]);

  return display;
}

export function LoyaltyFlashRewardBlock({ props: _props }: { props: LoyaltyFlashRewardProps }) {
  const { data, loading } = useLoyalty();

  if (loading) return null;
  if (!data?.activeFlashReward) return null;

  const reward = data.activeFlashReward;

  return <FlashRewardContent reward={reward} />;
}

function FlashRewardContent({ reward }: {
  reward: {
    id: string;
    title: string;
    description: string | null;
    imageUrl: string | null;
    endsAt: string;
    rewardType: string;
  };
}) {
  const countdown = useCountdown(reward.endsAt);

  const tagLabel = reward.rewardType === 'exclusive' ? 'EXCLUSIVE REWARD' : 'FLASH REWARD';
  const ctaLabel = reward.rewardType === 'ticket' ? 'Get My Ticket' : 'Claim Now';

  return (
    <div
      className="loyalty-card relative overflow-hidden rounded-xl"
      style={{ backgroundColor: 'var(--color-primary)' }}
    >
      {/* Background image if available */}
      {reward.imageUrl && (
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${reward.imageUrl})` }}
        />
      )}

      {/* Decorative accent */}
      <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/10 -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full bg-white/5 translate-y-1/2 -translate-x-1/2" />

      <div className="relative z-10 p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          {/* Content */}
          <div className="flex-1">
            {/* Tag */}
            <span className="loyalty-tag-skew inline-block text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-white/20 text-white mb-3 tracking-wider">
              &#9889; {tagLabel}
            </span>

            {/* Title */}
            <h2 className="loyalty-heading text-2xl sm:text-3xl text-white mb-2">
              {reward.title}
            </h2>

            {/* Description */}
            {reward.description && (
              <p className="text-white/80 text-sm mb-4 max-w-lg">
                {reward.description}
              </p>
            )}

            {/* Countdown */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-white/60 text-xs uppercase tracking-wider">Ends in</span>
              <div className="flex gap-1">
                {countdown.split(' ').map((part, i) => (
                  <span
                    key={i}
                    className="inline-block bg-white/20 backdrop-blur-sm text-white font-bold text-sm px-2 py-1 rounded"
                  >
                    {part}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex-shrink-0">
            <button className="px-6 py-3 rounded-lg bg-white text-[var(--color-primary)] font-bold text-sm hover:bg-white/90 transition-colors shadow-lg">
              {ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
